<template>
    <div ref="rootElement">
        <!-- Language Switcher -->
        <!--        <div class="absolute top-4 right-4 z-10">-->
        <!--            <LanguageSwitcher />-->
        <!--        </div>-->

        <div :class="paramsAreNotValid ? 'blur-sm' : ''" class="grid grid-cols-3 h-screen">
            <!-- Lewa sekcja (2/3 szerokości) -->
            <div class="col-span-2">
                <Configurator
                    ref="configuratorRef"
                    v-if="!containsTestConfig"
                    :uuid="uuid"
                    :imageData="imageData"
                    :colors="updateColors"
                    :colorsFile="updateColorsFile"
                    :textSettings="textSettings"
                    :textContent="textContent"
                    :imageSettings="updateImageSettings"
                    :downloadVisualizationTrigger="downloadVisualizationTrigger"
                    :downloadVisualizationType="downloadVisualizationType"
                    :qualityScore="qualityScore"
                    :confirmed="visualizationConfirmed"
                    :editAfterSave="editAfterSave"
                    @download:finished="handleDownloadFinished"
                    @remove:logo="removeLogo"
                    @visualization:loaded="visualizationLoaded"
                    @resize:logo="resizeLogo"
                    @remove:text="removeText"
                    @params:not-valid="paramsNotValid"
                    @update:extensions="handleExtensions"
                    @update:print_method="handlePrintMethod"
                    @update:grav_color="handleGravColor"
                    @history:colors="handleHistoryColors"
                    @crop:file-name="handleCropFileName"
                    @set:boundbox-validator="setBoundBoxValidator"
                    @set:boundbox-fitter="setBoundBoxFitter"
                    @set:remove-transformer-selection="setRemoveTransformerSelection"
                    @revert:font-size="handleRevertFontSize"
                    @revert:text-content="handleRevertTextContent"
                    @update:text-validation="handleTextValidation"
                    @update:visualization-confirmed="handleVisualizationConfirmed"
                    @update:edit-after-save="handleEditAfterSave"
                    @update:border-rotation="handleBorderRotation"
                    @logo-area:status="handleLogoAreaStatus"
                >
                </Configurator>
                <Configurator2 v-else></Configurator2>
            </div>

            <!-- Prawa sekcja (1/3 szerokości) -->
            <div class="col-span-1 flex flex-col h-screen min-h-0 overflow-hidden border-l-2 border-gray-400 z-[9] bg-white">
                <Settings
                    :uuid="uuid"
                    :removeLogo="removeLogoData"
                    :removeText="removeTextData"
                    :resizeLogo="resizeLogoData"
                    :extensions="extensions"
                    :historyColors="historyColors"
                    :cropFileName="cropFileName"
                    :visualization="visualization"
                    :printMethod="printMethod"
                    :gravColor="gravColor"
                    :boundBoxValidator="boundBoxValidator"
                    :boundBoxFitter="boundBoxFitter"
                    :removeTransformerSelection="removeTransformerSelection"
                    :revertFontSize="revertFontSize"
                    :revertTextContent="revertTextContent"
                    :textValidation="textValidation"
                    :confirmed="visualizationConfirmed"
                    :editAfterSave="editAfterSave"
                    :borderRotation="borderRotation"
                    :downloadPdfLoading="downloadPdfLoading"
                    :logoAreaStatus="logoAreaStatus"
                    @update:text-settings="handleTextSettings"
                    @update:text-content="handleTextContent"
                    @update:image-settings="handleImageSettings"
                    @update:image="handleImage"
                    @update:colors="handleUpdateColors"
                    @update:colors-file="handleUpdateColorsFile"
                    @update:quality-score="handleQualityScore"
                    @download:visualization="handleDownloadVisualization"
                    @update:visualization-confirmed="handleVisualizationConfirmed"
                    @update:edit-after-save="handleEditAfterSave"
                    @save:visualization="handleSaveVisualization"
                    @save:visualization-snap="handleSaveVisualizationSnap"
                    @save:production-svg="handleSaveProductionSvg"
                    @remove:text-content="handleRemoveTextContent"
                ></Settings>
            </div>
        </div>


        <div v-if="paramsAreNotValid">
            <div class="absolute inset-0 flex items-center justify-center">
                <div class="bg-gray-800 text-red-500 border-2 border-red-500 p-6 rounded-lg shadow-lg">
                    <h1 class="text-2xl font-semibold">{{ errorMessage }}</h1>
                </div>
            </div>
        </div>
    </div>


</template>

<script setup>
import Configurator from "./Configurator.vue";
import Settings from "./Settings.vue";
import TextLayer from "./settings/TextLayer.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import {ref, computed, onMounted, onUnmounted, nextTick, watch} from "vue";
import Configurator2 from "./Configurator2.vue";
import {useTranslation} from '../composables/useTranslation.js';


const {$t} = useTranslation()

const imageData = ref(null)
const uuid = ref(null)
const extensions = ref(null)
const printMethod = ref(null)
const gravColor = ref(null)
const paramsAreNotValid = ref(false)
const removeLogoData = ref(null)
const resizeLogoData = ref(null)
const visualization = ref(null)
const updateColors = ref(null)
const updateColorsFile = ref(null)
const removeTextData = ref(null)
const textSettings = ref(null)
const textContent = ref(null)
const updateImageSettings = ref(null)
const historyColors = ref(null)
const cropFileName = ref(null)
const visualizationConfirmed = ref(false)
const editAfterSave = ref(false)
const configuratorRef = ref(null)

const errorMessage = ref('')

onMounted(async () => {
    errorMessage.value = await $t('errors.invalid_parameters', 'Podano błędne parametry')
})
const downloadVisualizationTrigger = ref(0)
const downloadVisualizationType = ref(null)
const boundBoxValidator = ref(null)
const boundBoxFitter = ref(null)
const removeTransformerSelection = ref(null)
const revertFontSize = ref(null)
const revertTextContent = ref(null)
const textValidation = ref(true)
const qualityScore = ref(null)
const borderRotation = ref(0)
const downloadPdfLoading = ref(false)
const rootElement = ref(null)
const logoAreaStatus = ref(null)
let heightInterval = null
let resizeObserver = null
let lastSentHeight = 0

const getContentHeight = () => {
    if (!rootElement.value) {
        return Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        ) + 300;
    }

    const rect = rootElement.value.getBoundingClientRect();
    const scrollHeight = rootElement.value.scrollHeight;

    // return Math.max(rect.height, scrollHeight)  ;
    return 800;
}

const sendHeight = () => {
    const height = getContentHeight();

    if (height !== lastSentHeight) {
        lastSentHeight = height;
        window.parent.postMessage({
            type: 'APP-HEIGHT',
            payload: height
        }, '*');
    }
}

onMounted(() => {
    uuid.value = Array.from({length: 8}, () =>
        String.fromCharCode(Math.floor(Math.random() * 26) + (Math.random() < 0.5 ? 65 : 97))
    ).join('');

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('uuid')) {
        uuid.value = urlParams.get('uuid');
    }

    window.parent.postMessage({
        type: 'UUID',
        payload: uuid.value
    }, '*');

    sendHeight();

    resizeObserver = new ResizeObserver(() => {
        sendHeight();
    });

    resizeObserver.observe(document.body);
    resizeObserver.observe(document.documentElement);

    nextTick(() => {
        if (rootElement.value) {
            resizeObserver.observe(rootElement.value);
        }
    });

    heightInterval = setInterval(sendHeight, 2000);

    setTimeout(sendHeight, 100);
    setTimeout(sendHeight, 500);
    setTimeout(sendHeight, 1000);
})

onUnmounted(() => {
    if (heightInterval) {
        clearInterval(heightInterval);
    }
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
})


const containsTestConfig = computed(() => {
    return window.location.href.includes('test-config');
});

const handleTextSettings = (data) => {
    textSettings.value = data;
}

const handleTextContent = (data) => {
    textContent.value = data;
}

const handleImage = (data, changedOriginalLogo) => {
    if (
        (data &&
            typeof data === 'object' &&
            Object.keys(data).length === 0) || !data
    ) {
        logoAreaStatus.value = {exceeded: false, maxArea: 0, currentArea: 0};
    }

    if (configuratorRef.value && changedOriginalLogo === true) {
        configuratorRef.value.forceChangeOriginalLogo();
    }
    imageData.value = data;
}

const handleExtensions = (data) => {
    extensions.value = data;
}

const handlePrintMethod = (data) => {
    printMethod.value = data;
}
const handleGravColor = (data) => {
    gravColor.value = data;
}
const removeLogo = (data) => {
    removeLogoData.value = Date.now();
    logoAreaStatus.value = {exceeded: false, maxArea: 0, currentArea: 0};
}
const resizeLogo = (data) => {
    resizeLogoData.value = data;
}
const handleBorderRotation = (rotation) => {
    borderRotation.value = rotation;
}

const visualizationLoaded = (data) => {
    visualization.value = data;
}
const handleUpdateColors = (data) => {
    updateColors.value = data;
}
const handleUpdateColorsFile = (replaceColorImagePreview, historyColorObject) => {
    updateColorsFile.value = replaceColorImagePreview;
    updateColors.value = historyColorObject;
}
const paramsNotValid = (data) => {
    paramsAreNotValid.value = true;
}
const handleDownloadVisualization = (type) => {
    downloadPdfLoading.value = type === 'pdf';
    downloadVisualizationType.value = type;
    downloadVisualizationTrigger.value = Math.random();
}

const handleDownloadFinished = ({type} = {}) => {
    if (type === 'pdf') {
        downloadPdfLoading.value = false;
    }
}

const removeText = (data) => {
    removeTextData.value = Date.now();
}

const handleImageSettings = (data) => {
    updateImageSettings.value = data;
}
const handleHistoryColors = (data) => {
    historyColors.value = data.colors;
}

const handleCropFileName = (cropFileNameData) => {
    cropFileName.value = cropFileNameData;
    if (updateColors.value) {
        updateColors.value = {
            ...updateColors.value,
            file: cropFileNameData,
        };
    }
}

const setBoundBoxValidator = (data) => {
    boundBoxValidator.value = data;
}

const setBoundBoxFitter = (data) => {
    boundBoxFitter.value = data;
}

const setRemoveTransformerSelection = (fn) => {
    removeTransformerSelection.value = fn;
}

const handleRevertFontSize = (fontSize) => {
    revertFontSize.value = fontSize
}

const handleRevertTextContent = (text) => {
    // Ustawiamy tekst cofnięty jako źródło prawdy
    textContent.value = text
    revertTextContent.value = text
}

const handleTextValidation = (isValid) => {
    textValidation.value = isValid
}

watch(revertTextContent, (newVal, oldVal) => {
    if (newVal !== null && newVal !== undefined && newVal !== oldVal) {
        textValidation.value = false;
    }
}, {immediate: false})

const handleQualityScore = (score) => {
    qualityScore.value = score
}

const handleVisualizationConfirmed = (confirmed) => {
    visualizationConfirmed.value = confirmed
}

const handleEditAfterSave = (value) => {
    editAfterSave.value = value
}

const finishSaveHandler = (done) => {
    if (typeof done === 'function') {
        done();
    }
}

const handleSaveVisualization = async (done) => {
    try {
        if (configuratorRef.value) {
            await configuratorRef.value.handleSaveVisualizationAndConfirm();
        }
    } finally {
        finishSaveHandler(done);
    }
}

const handleSaveVisualizationSnap = async (done) => {
    try {
        if (configuratorRef.value) {
            await configuratorRef.value.saveVisualizationSnap();
        }
    } finally {
        finishSaveHandler(done);
    }
}

const handleSaveProductionSvg = async (done) => {
    try {
        if (configuratorRef.value) {
            await configuratorRef.value.uploadProductionSvg();
        }
    } finally {
        finishSaveHandler(done);
    }
}
const handleRemoveTextContent = () => {
    if (configuratorRef.value) {
        configuratorRef.value.removedTextContentHideText();
    }
}

const handleLogoAreaStatus = (status) => {
    logoAreaStatus.value = status;
}
</script>
